import dayjs from "dayjs";
import Axios from "helpers/Interceptor";
import { EmailGrowth, SpotifyGrowth } from "modules/Types";
import { GeographicBreakdown } from "modules/Types";


/** Interface for platform data response */
export interface PlatformDataResponse {
    platform: string;
    stat: string;
    chartData: Array<{
        datetime: string;
        fanbase_count: string;
    }>;
    growthData: {
        currentGrowthValue: number;
        percentage?: number;
    };
}

/** Props for fetching Instagram insights data */
interface GetInstagramDataProps {
    /** Brand ID to fetch data for */
    brandId: string | number;
    /** Start date for date range in YYYY-MM-DD format */
    startDate: string;
    /** End date for date range in YYYY-MM-DD format */
    endDate: string;
}

/** Instagram insights response data structure */
interface InstagramInsightsData {
    /** Instagram engagement metrics and stats */
    insights: {
        followers: number;
        engagement: number;
        // ... other Instagram metrics
    };
    /** Post-related data if requested */
    posts?: Array<{
        id: string;
        engagement: number;
        // ... other post fields
    }>;
}

/** Props for fetching fanbase data */
interface GetFanbaseDataProps {
    /** Brand ID to fetch data for */
    brandId: string | number;
    /** Platform ID (e.g. spotify, instagram) */
    platformId: string;
    /** Specific stat identifier to fetch */
    statIdentifier: string;
    /** Start date for date range in YYYY-MM-DD format */
    startDate: string;
    /** End date for date range in YYYY-MM-DD format */
    endDate: string;
}

/** Props for fetching website data */
interface GetWebsiteDataProps {
    /** Brand slug to fetch data for */
    brandSlug: string;
    /** Optional content slug to filter by */
    contentSlug?: string;
    /** Start date for date range in YYYY-MM-DD format */
    startDate?: string | null;
    /** End date for date range in YYYY-MM-DD format */
    endDate?: string | null;
}

/** Website analytics data response structure */
export interface WebsiteDashboardData {
    /** Overall activity metrics over time */
    overallActivity: {
        date: string;
        pageViews: number;
        streamingLinkClicks: number;
        signups: number;
        tourLinkClicks: number;
    }[];

    /** Top level summary metrics */
    topLevelMetrics: {
        /** Total page visits */
        pageVisits: number;
        /** Total streaming link clicks */
        streamingLinkClicks: number;
        /** Click-through rate as decimal */
        ctr: number;
    };

    /** Breakdown of content performance */
    contentBreakdown: {
        count: number;
        image: string;
        name: string;
        slug: string;
    }[];

    /** Breakdown of streaming services usage */
    streamingServicesBreakdown: {
        count: number;
        percent: number;
        service: string;
    }[];

    /** Traffic source referrers */
    referrers: {
        count: number;
        referrer?: string;
    }[];

    /** Geographic distribution of visitors */
    geographicBreakdown: GeographicBreakdown;
};

/** Props for fetching Spotify growth data */
interface GetSpotifyGrowthProps {
    /** Start date for date range */
    startDate: string;
    /** End date for date range */
    endDate: string;
    /** Brand ID to fetch data for */
    brandId: number;
}


/** Props for fetching email growth data */
interface GetEmailGrowthProps {
    /** Start date for date range */
    startDate: string;
    /** End date for date range */
    endDate: string;
    /** Brand identifier */
    brand: string;
    /** Content name identifier */
    name: string;
}



/** Response type for dashboard data refresh */
interface DashboardRefreshResponse {
    /** Whether the refresh was successful */
    success: boolean;
    /** Timestamp of the refresh */
    refreshedAt: string;
}

/**
 * Fetches Instagram insights data including followers, engagement and posts for a given date range
 * @param props - The props object containing brandId and date range
 * @returns Instagram insights data including metrics and posts
 */
export async function getInstagramData_api(props: GetInstagramDataProps) {
    const {
        brandId,
        startDate,
        endDate
    } = props

    const url = `/dashboard/brand/${brandId}/instagram-insights?startDate=${startDate}&endDate=${endDate}&options[posts]=true`
    const response = await Axios.get<InstagramInsightsData>(url)
    return response.data
}

/**
 * Fetches fanbase metrics data for a specific platform and stat over a date range
 * @param props - The props object containing brandId, platform details and date range
 * @returns Fanbase metrics data including chart data and growth statistics
 */
export async function getFanbaseData_api(props: GetFanbaseDataProps) {
    const {
        brandId,
        platformId,
        statIdentifier,
        startDate,
        endDate
    } = props

    const url = `/brand/${brandId}/platform-stats/${platformId}/${statIdentifier}?startDate=${startDate}&endDate=${endDate}`
    const response = await Axios.get<PlatformDataResponse>(url)
    return response.data
}
/**
 * Fetches website analytics data for a brand, optionally filtered by content
 * @param props - The props object containing brandSlug and optional filters
 * @returns Website analytics data for the specified parameters
 */
export async function getWebsiteData_api(props: GetWebsiteDataProps) {
    const {
        brandSlug,
        contentSlug,
        startDate,
        endDate
    } = props

    const url = contentSlug ?
        `/dashboard/brand/${brandSlug}/website-analytics/${contentSlug}`
        :
        `/dashboard/brand/${brandSlug}/website-analytics/overall`

    const userTimezone = dayjs.tz.guess()

    const response = await Axios.get<WebsiteDashboardData>(url, {
        params: {
            startDate,
            endDate,
            timezone: userTimezone
        }
    })

    return response.data
}


/**
 * Retrieves overall website analytics data for a brand within an optional date range
 * @async
 * @param {string} brandSlug - The slug/URL-friendly name of the brand
 * @param {string} [startDate] - Optional start date for filtering data
 * @param {string} [endDate] - Optional end date for filtering data
 * @returns {Promise<WebsiteDashboardData | null>} The website analytics data or null if error occurs
 */
export const getWebsiteOverallData_api = async (brandSlug: string, startDate?: string, endDate?: string): Promise<WebsiteDashboardData | null> => {
    try {
        let url = `/dashboard/brand/${brandSlug}/website-analytics/overall`
        if (startDate && endDate) url += `?startDate=${startDate}&endDate=${endDate}`

        const response = await Axios.get<WebsiteDashboardData>(url)
        return response.data
    } catch (error) {
        return null
    }
}

/**
 * Refreshes the fanbase data for a brand
 * @param brandId - The ID of the brand to refresh data for
 * @returns Promise resolving to the API response data
 */
export const refreshDashboardDataRemote_api = async (brandId: number): Promise<DashboardRefreshResponse> => {
    const url = `/dashboard/brand/${brandId}/fanbase-refresh`
    const response = await Axios.get<DashboardRefreshResponse>(url)
    return response.data
}

/**
 * Fetches Spotify follower growth data for a brand within a date range
 * @param props - Object containing date range and brand ID
 * @returns Spotify growth data or null if error occurs
 */
export const getSpotifyGrowth_api = async (props: GetSpotifyGrowthProps): Promise<SpotifyGrowth | null> => {
    const { startDate, endDate, brandId } = props;
    try {
        const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
        const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
        const url = `/brand/${brandId}/platform-stats/spotify/followers?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

        const response = await Axios.get<SpotifyGrowth>(url);
        return response?.data || null;
    } catch (error) {
        console.log('Error fetching Spotify growth:', error);
        return null;
    }
};

/**
 * Fetches email growth data for a brand's content within a date range
 * @param props - Object containing date range and brand/content identifiers
 * @returns Email growth data or null if error occurs
 */
export const getEmailGrowth_api = async (props: GetEmailGrowthProps): Promise<EmailGrowth | null> => {
    const { startDate, endDate, brand, name } = props;
    try {
        const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
        const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
        const url = `/dashboard/brand/${brand}/content/${name}/email/adds?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

        const response = await Axios.get<EmailGrowth>(url);
        return response.data;
    } catch (error) {
        console.log('Error fetching email growth:', error);
        return null;
    }
};

/** Props for fetching platform stats data */
interface GetPlatformStatsProps {
    /** Brand slug */
    brandSlug: string;
    /** Brand ID or slug */
    brandId: number;
    /** Content slug if applicable */
    contentSlug?: string;
    /** Campaign ID if applicable */
    campaignId?: string | number;
    /** Start date for date range */
    startDate: string;
    /** End date for date range */
    endDate: string;
}

/**
 * Fetches Spotify streams data for a specific content
 */
export const getSpotifyStreams_api = async (props: GetPlatformStatsProps) => {
    const { brandSlug, contentSlug, startDate, endDate } = props;
    const url = `/dashboard/brand/${brandSlug}/content/${contentSlug}/spotify/streams`;
    const response = await Axios.get(url, { params: { startDate, endDate } });
    return response.data;
};

/**
 * Fetches Shazam data for a specific content
 */
export const getShazamStats_api = async (props: GetPlatformStatsProps) => {
    const { brandSlug, contentSlug, startDate, endDate } = props;
    const url = `/dashboard/brand/${brandSlug}/content/${contentSlug}/shazam/shazams`;
    const response = await Axios.get(url, { params: { startDate, endDate } });
    return response.data;
};

/**
 * Fetches Spotify playlist follower data
 */
export const getSpotifyPlaylistFollowers_api = async (props: GetPlatformStatsProps) => {
    const { brandSlug, contentSlug, startDate, endDate } = props;
    const url = `/dashboard/brand/${brandSlug}/content/${contentSlug}/spotify/followers`;
    const response = await Axios.get(url, { params: { startDate, endDate } });
    return response.data;
};

/**
 * Fetches Instagram post metrics for a campaign
 */
export const getInstagramPostMetrics_api = async (props: GetPlatformStatsProps & { metric: 'reach' | 'total_interactions' }) => {
    const { brandSlug, campaignId, startDate, endDate, metric } = props;
    const url = `/dashboard/brand/${brandSlug}/campaign/${campaignId}/content/${metric}`;
    const response = await Axios.get(url, { params: { startDate, endDate } });
    return response.data;
};

/**
 * Fetches Instagram profile metrics
 */
export const getInstagramProfileMetrics_api = async (props: GetPlatformStatsProps & { metric: 'reach' | 'profile_views' }) => {
    const { brandId, startDate, endDate, metric } = props;
    const url = `/brand/${brandId}/platform-stats/instagram/${metric}`;
    const response = await Axios.get(url, { params: { startDate, endDate } });
    return response.data;
};